<template>
  <div class="space-y-4">
    <!-- Bridge Header -->
    <div class="flex items-center justify-between pb-2 border-b">
      <div class="flex items-center space-x-3">
        <Button
          variant="ghost"
          size="sm"
          @click="$emit('close')"
        >
          <ArrowLeft class="h-4 w-4" />
        </Button>
        <div>
          <h2 class="text-lg font-semibold">{{ bridge.name }}</h2>
          <div v-if="isEditingIP" class="flex items-center space-x-2 mt-1">
            <Input
              v-model="editIPValue"
              class="h-7 text-sm w-40"
              placeholder="IP Address"
              @keyup.enter="saveIP"
              @keyup.escape="cancelEditIP"
            />
            <Button variant="ghost" size="sm" class="h-7 w-7 p-0" @click="saveIP" :disabled="isSavingIP">
              <Check class="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="sm" class="h-7 w-7 p-0" @click="cancelEditIP">
              <X class="h-3 w-3" />
            </Button>
          </div>
          <div v-else class="flex items-center space-x-1 group">
            <p class="text-sm text-muted-foreground">{{ currentIP }}</p>
            <Button variant="ghost" size="sm" class="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity" @click="startEditIP">
              <Pencil class="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
      <Badge variant="secondary">{{ totalLights }} lights</Badge>
    </div>

    <!-- Quick Actions -->
    <div class="flex space-x-2">
      <Button
        variant="outline"
        size="sm"
        @click="refreshLights"
        :disabled="isRefreshing"
      >
        <RefreshCw class="h-4 w-4 mr-2" :class="{ 'animate-spin': isRefreshing }" />
        Refresh
      </Button>
      <Button
        variant="outline"
        size="sm"
        @click="toggleAllLights"
      >
        <Lightbulb class="h-4 w-4 mr-2" />
        {{ allLightsOn ? 'Turn Off All' : 'Turn On All' }}
      </Button>
    </div>

    <!-- Rooms and Lights -->
    <ScrollArea class="h-[600px]">
      <div class="space-y-4 pr-4">
        <!-- Rooms -->
        <div 
          v-for="room in roomGroups"
          :key="room.name"
          class="space-y-3"
        >
          <!-- Room Card -->
          <div class="bg-card border rounded-lg p-4 space-y-3">
            <!-- Room Header -->
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium">{{ room.name === 'Other Lights' ? 'Other Lights' : room.name }}</h3>
              <div class="flex items-center space-x-2">
                <span class="text-xs text-muted-foreground">
                  {{ room.lights.length }} lights
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  @click="toggleRoomLights(room)"
                  class="h-6 w-6 p-0"
                >
                  <Lightbulb class="h-3 w-3" />
                </Button>
              </div>
            </div>

            <!-- Room Lights -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              <div
                v-for="light in room.lights"
                :key="light.id"
                class="flex items-center gap-2 min-w-0"
              >
                <div class="flex-1 min-w-0 overflow-hidden">
                  <LightColorPip
                    :light="light"
                    @click="toggleLight(light)"
                  />
                </div>
                <Switch 
                  :modelValue="light.used_in_setup"
                  @update:modelValue="lightsStore.toggleLightInSetup(light.id)"
                  class="flex-shrink-0"
                  :title="light.used_in_setup ? 'Remove from setup' : 'Add to setup'"
                />
              </div>
            </div>

            <!-- Room Scenes -->
            <div v-if="room.scenes.length > 0" class="space-y-2">
              <div class="flex items-center justify-between">
                <h4 class="text-xs font-medium text-muted-foreground">Scenes</h4>
                <span class="text-xs text-muted-foreground">{{ room.scenes.length }}</span>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                <SceneColorSquare
                  v-for="scene in room.scenes"
                  :key="scene.id"
                  :scene="scene"
                  :is-active="activeScenes.has(scene.id)"
                  @activate="activateScene(scene)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Loader2 class="h-6 w-6 animate-spin mr-2" />
      <span class="text-sm text-muted-foreground">Loading lights and scenes...</span>
    </div>

    <!-- Error State -->
    <div v-if="error" class="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
      <p class="text-sm text-destructive">{{ error }}</p>
      <Button
        variant="outline"
        size="sm"
        @click="retry"
        class="mt-2"
      >
        Try Again
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger'
import { computed, ref, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrowLeft, RefreshCw, Lightbulb, Loader2, Pencil, Check, X } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import LightColorPip from './LightColorPip.vue'
import SceneColorSquare from './SceneColorSquare.vue'
import { useLightsStore, type Light } from '@/stores/lights'
import { useHueBridgesStore } from '@/stores/hueBridges'
import { storeToRefs } from 'pinia'

interface HueScene {
  id: string
  name: string
  type?: 'LightScene' | 'GroupScene'
  lights?: Array<{
    light_id: string
    color?: string
    brightness?: number
    on: boolean
  }>
  metadata?: Record<string, unknown>
}

interface Props {
  bridge: {
    id: string
    name: string
    ip_address: string
    bridge_id: string
  }
}

const props = defineProps<Props>()

defineEmits<{
  close: []
}>()

// Stores
const lightsStore = useLightsStore()
const bridgesStore = useHueBridgesStore()
const { allLights } = storeToRefs(lightsStore)

// Local state
const isLoading = ref(false)
const isRefreshing = ref(false)
const error = ref<string | null>(null)
const activeScenes = ref<Set<string>>(new Set())
const isEditingIP = ref(false)
const editIPValue = ref('')
const isSavingIP = ref(false)

// IP from store (reactive to updates)
const currentIP = computed(() => {
  const storeBridge = bridgesStore.getBridge(props.bridge.bridge_id)
  return storeBridge?.ip_address ?? props.bridge.ip_address
})

const startEditIP = () => {
  editIPValue.value = currentIP.value
  isEditingIP.value = true
}

const cancelEditIP = () => {
  isEditingIP.value = false
  editIPValue.value = ''
}

const saveIP = async () => {
  const ip = editIPValue.value.trim()
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  if (!ipRegex.test(ip)) {
    error.value = 'Invalid IP address format'
    return
  }
  try {
    isSavingIP.value = true
    await bridgesStore.updateBridgeIP(props.bridge.bridge_id, ip)
    isEditingIP.value = false
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to update IP'
  } finally {
    isSavingIP.value = false
  }
}

// Computed properties
const bridgeLights = computed(() => {
  return allLights.value.filter(light => light.hue_bridge_id === props.bridge.bridge_id)
})

const totalLights = computed(() => bridgeLights.value.length)

const allLightsOn = computed(() => {
  return bridgeLights.value.length > 0 && bridgeLights.value.every(light => light.current_state.on)
})

const roomGroups = computed(() => {
  const groups = new Map<string, { name: string; lights: Light[]; scenes: HueScene[] }>()
  
  bridgeLights.value.forEach(light => {
    // Use "Other Lights" for lights without a room_name or with empty/null room_name
    const roomName = (light.room_name && light.room_name.trim()) || 'Other Lights'
    
    if (!groups.has(roomName)) {
      groups.set(roomName, {
        name: roomName,
        lights: [],
        scenes: []
      })
    }
    groups.get(roomName)!.lights.push(light)
  })
  
  // TODO: Once scenes are stored in database, fetch them here
  // For now, scenes will be empty but the UI structure is ready
  
  // Sort rooms: "Other Lights" comes last, rest alphabetically
  return Array.from(groups.values()).sort((a, b) => {
    if (a.name === 'Other Lights') return 1
    if (b.name === 'Other Lights') return -1
    return a.name.localeCompare(b.name)
  })
})

// Methods
const refreshLights = async () => {
  try {
    isRefreshing.value = true
    error.value = null
    await bridgesStore.syncAllLightsFromBridge(props.bridge.bridge_id)
    // Fetch updated lights from database
    await lightsStore.fetchLights()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to refresh lights'
  } finally {
    isRefreshing.value = false
  }
}

const toggleLight = async (light: Light) => {
  try {
    await lightsStore.updateLightState(light.id, {
      on: !light.current_state.on
    })
  } catch (err) {
    logger.error('Failed to toggle light:', err)
  }
}

const toggleAllLights = async () => {
  try {
    const newState = !allLightsOn.value
    await Promise.all(
      bridgeLights.value.map(light => 
        lightsStore.updateLightState(light.id, { on: newState })
      )
    )
  } catch (err) {
    logger.error('Failed to toggle all lights:', err)
  }
}

const toggleRoomLights = async (room: { lights: Light[] }) => {
  try {
    const roomLightsOn = room.lights.every((light) => light.current_state.on)
    const newState = !roomLightsOn
    
    await Promise.all(
      room.lights.map((light) => 
        lightsStore.updateLightState(light.id, { on: newState })
      )
    )
  } catch (err) {
    logger.error('Failed to toggle room lights:', err)
  }
}

const activateScene = async (scene: HueScene) => {
  try {
    // TODO: Implement scene activation when scene store is ready
    logger.info('Activating scene:', scene.name)
    activeScenes.value.add(scene.id)
    
    // Remove from active scenes after a delay (visual feedback)
    setTimeout(() => {
      activeScenes.value.delete(scene.id)
    }, 2000)
  } catch (err) {
    logger.error('Failed to activate scene:', err)
  }
}

const retry = () => {
  error.value = null
  refreshLights()
}

// Lifecycle
onMounted(async () => {
  // Always fetch lights from database first
  await lightsStore.fetchLights()
  
  // If still no lights for this bridge, sync from bridge
  if (bridgeLights.value.length === 0) {
    await refreshLights()
  }
})
</script>